// This component is meant to replace modify the default MTableHeader of material-table
// This enables the click feature on action's header item, which is disabled on the original component and can't be altered

/* eslint-disable no-unused-vars */
import * as React from 'react';
import PropTypes from 'prop-types';
import { TableHead, TableRow, TableCell, TableSortLabel, Tooltip, withStyles } from '@material-ui/core';
import * as CommonValues from './common-values';

/* eslint-enable no-unused-vars */

export class CustomMTableHeader extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      lastX:             0,
      resizingColumnDef: undefined,
    };
  }

  componentDidMount() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  handleMouseDown = (e, columnDef) => {
    this.setState({
      lastAdditionalWidth: columnDef.tableData.additionalWidth,
      lastX:               e.clientX,
      resizingColumnDef:   columnDef,
    });
  };

  handleMouseMove = (e) => {
    if (!this.state.resizingColumnDef) {
      return;
    }

    let additionalWidth = this.state.lastAdditionalWidth + e.clientX - this.state.lastX;

    additionalWidth = Math.min(
      this.state.resizingColumnDef.maxWidth || additionalWidth,
      additionalWidth,
    );

    if (this.state.resizingColumnDef.tableData.additionalWidth !== additionalWidth) {
      this.props.onColumnResized(
        this.state.resizingColumnDef.tableData.id,
        additionalWidth,
      );
    }
  };

  handleMouseUp = () => {
    this.setState({ resizingColumnDef: undefined });
  };

  getCellStyle = (columnDef) => {
    const width = CommonValues.reducePercentsInCalc(
      columnDef.tableData.width,
      this.props.scrollWidth,
    );

    const style = {
      ...this.props.headerStyle,
      ...columnDef.headerStyle,
      boxSizing: 'border-box',
      width,
      maxWidth:  columnDef.maxWidth,
      minWidth:  columnDef.minWidth,
    };

    if (this.props.options.tableLayout === 'fixed' && this.props.options.columnResizable && columnDef.resizable !== false) {
      style.paddingRight = 2;
    }

    return style;
  };

  renderHeader() {
    const size = this.props.options.padding === 'default' ? 'medium' : 'small';

    const mapArr = this.props.columns
      .filter((columnDef) => !columnDef.hidden && !(columnDef.tableData.groupOrder > -1))
      .sort((a, b) => a.tableData.columnOrder - b.tableData.columnOrder)
      .map((columnDef) => {
        let content = columnDef.title;

        if (columnDef.tooltip) {
          content = (
            <Tooltip title={columnDef.tooltip} placement="bottom">
              <span>{content}</span>
            </Tooltip>
          );
        }

        const cellAlignment = columnDef.align !== undefined
          ? columnDef.align
          : ['numeric', 'currency'].indexOf(columnDef.type) !== -1
            ? 'right'
            : 'left';
        return (
          <TableCell
            key={columnDef.tableData.id}
            align={cellAlignment}
            className={this.props.classes.header}
            style={this.getCellStyle(columnDef)}
            size={size}
          >
            {content}
          </TableCell>
        );
      });
    return mapArr;
  }

  renderActionsHeader() {
    const localization = {
      ...CustomMTableHeader.defaultProps.localization,
      ...this.props.localization,
    };
    const width = CommonValues.actionsColumnWidth(this.props);
    return (
      <TableCell
        key="key-actions-column"
        padding="checkbox"
        className={this.props.classes.header}
        style={{
          ...this.props.headerStyle,
          width,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        <TableSortLabel hideSortIcon>
          {localization.actions}
        </TableSortLabel>
      </TableCell>
    );
  }

  renderDetailPanelColumnCell() {
    return (
      <TableCell
        padding="none"
        key="key-detail-panel-column"
        className={this.props.classes.header}
        style={{ ...this.props.headerStyle }}
      />
    );
  }

  render() {
    const headers = this.renderHeader();

    if (this.props.showActionsColumn) {
      if (this.props.actionsHeaderIndex >= 0) {
        let endPos = 0;
        if (this.props.hasSelection) {
          endPos = 1;
        }
        headers.splice(
          this.props.actionsHeaderIndex + endPos,
          0,
          this.renderActionsHeader(),
        );
      } else if (this.props.actionsHeaderIndex === -1) {
        headers.push(this.renderActionsHeader());
      }
    }

    this.props.columns
      .filter((columnDef) => columnDef.tableData.groupOrder > -1)
      .forEach((columnDef) => {
        headers.splice(
          0,
          0,
          <TableCell
            padding="checkbox"
            key={`key-group-header${columnDef.tableData.id}`}
            className={this.props.classes.header}
          />,
        );
      });

    return (
      <TableHead>
        <TableRow>{headers}</TableRow>
      </TableHead>
    );
  }
}

CustomMTableHeader.defaultProps = {
  hasSelection: false,
  headerStyle:  {},
  classes:      {
    header: '',
  },
  localization: {
    actions: 'Actions',
  },
  actionsHeaderIndex: 0,
};

CustomMTableHeader.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  classes: PropTypes.shape({
    header: PropTypes.string,
  }),
  options: PropTypes.shape({
    padding:         PropTypes.string,
    tableLayout:     PropTypes.string,
    columnResizable: PropTypes.bool,
  }).isRequired,
  scrollWidth:        PropTypes.number.isRequired,
  hasSelection:       PropTypes.bool,
  headerStyle:        PropTypes.shape({}),
  localization:       PropTypes.shape({}),
  actionsHeaderIndex: PropTypes.number,
  showActionsColumn:  PropTypes.bool.isRequired,
  onColumnResized:    PropTypes.func.isRequired,
};

export const styles = (theme) => ({
  header: {
    // display: 'inline-block',
    position:        'sticky',
    top:             0,
    zIndex:          10,
    backgroundColor: theme.palette.background.paper, // Change according to theme,
  },
});

export default withStyles(styles, { withTheme: true })(CustomMTableHeader);
